import * as z from "zod";

export const atomicItemSchema = z.object({
  id: z.string(),
  itemTitle: z.string(),
  mainItem: z.string().nullable(),
});

export type AtomicItem = z.infer<typeof atomicItemSchema>;

export const atomicPhraseSchema = z.object({
  id: z.string(),
  bucketType: z.string(),
  phraseText: z.string(),
  scope: z.enum(["org", "survey"]),
  actionPhraseId: z.string().nullish(),
  subItemIds: z.array(z.string()).optional(),
  defaultSelected: z.boolean().optional(),
  supportsNegation: z.boolean().optional(),
  supportsQualifier: z.boolean().optional(),
  defaultNegation: z.enum(["", "not", "no"]).nullish(),
  qualifierPosition: z
    .enum(["before_finding", "after_no"])
    .nullable()
    .optional(),
});

export type AtomicPhrase = z.infer<typeof atomicPhraseSchema>;

export const listAtomicItemsInputSchema = z.object({
  organizationId: z.string(),
});

export type ListAtomicItemsInput = z.infer<typeof listAtomicItemsInputSchema>;

export const listAtomicItemsResponseSchema = z.object({
  items: z.array(atomicItemSchema),
});

export type ListAtomicItemsResponse = z.infer<
  typeof listAtomicItemsResponseSchema
>;

export const atomicItemLibraryEntrySchema = z.object({
  atomicItemId: z.string(),
  name: z.string(),
});

export const listAtomicItemLibraryResponseSchema = z.object({
  items: z.array(atomicItemLibraryEntrySchema),
});

export type ListAtomicItemLibraryResponse = z.infer<
  typeof listAtomicItemLibraryResponseSchema
>;

export const createAtomicItemInputSchema = z.object({
  organizationId: z.string(),
  name: z.string().trim().min(1).max(120),
});

export type CreateAtomicItemInput = z.infer<typeof createAtomicItemInputSchema>;

export const createAtomicItemResponseSchema = z.object({
  item: atomicItemLibraryEntrySchema,
});

export type CreateAtomicItemResponse = z.infer<
  typeof createAtomicItemResponseSchema
>;

export const updateAtomicItemInputSchema = z.object({
  organizationId: z.string(),
  name: z.string().trim().min(1).max(120),
});

export type UpdateAtomicItemInput = z.infer<typeof updateAtomicItemInputSchema>;

export const updateAtomicItemResponseSchema = z.object({
  item: atomicItemLibraryEntrySchema,
});

export type UpdateAtomicItemResponse = z.infer<
  typeof updateAtomicItemResponseSchema
>;

export const getAtomicItemPhrasesInputSchema = z.object({
  organizationId: z.string(),
  surveyId: z.string().optional(),
});

export type GetAtomicItemPhrasesInput = z.infer<
  typeof getAtomicItemPhrasesInputSchema
>;

export const getAtomicItemPhrasesResponseSchema = z.object({
  item: atomicItemSchema,
  phrases: z.array(atomicPhraseSchema),
  mainItemOverride: z.string().nullable(),
});

export type GetAtomicItemPhrasesResponse = z.infer<
  typeof getAtomicItemPhrasesResponseSchema
>;

export const atomicBucketTypeSchema = z.enum([
  "main_item",
  "sub_item",
  "quantity",
  "descriptor",
  "action",
  "disclaimer",
  "check",
  "location",
  "qualifier",
  "finding",
  "recommendation",
]);

export const createSurveyAtomicPhraseInputSchema = z.object({
  organizationId: z.string(),
  surveyId: z.string().optional(),
  bucketType: atomicBucketTypeSchema.exclude(["main_item"]),
  phraseText: z.string().min(1),
  scope: z.enum(["org", "survey"]).default("survey"),
  actionPhraseId: z.string().nullish().optional(),
  subItemIds: z.array(z.string()).optional(),
  supportsNegation: z.boolean().optional(),
  supportsQualifier: z.boolean().optional(),
  defaultNegation: z.enum(["", "not", "no"]).optional(),
  qualifierPosition: z.enum(["before_finding", "after_no"]).optional(),
});

export const updateAtomicFindingPhraseMetaInputSchema = z.object({
  organizationId: z.string(),
  actionPhraseId: z.string().nullish().optional(),
  supportsNegation: z.boolean().optional(),
  supportsQualifier: z.boolean().optional(),
  defaultNegation: z.enum(["", "not", "no"]).optional(),
  qualifierPosition: z.enum(["before_finding", "after_no"]).optional(),
});

export type UpdateAtomicFindingPhraseMetaInput = z.infer<
  typeof updateAtomicFindingPhraseMetaInputSchema
>;

export type CreateSurveyAtomicPhraseInput = z.infer<
  typeof createSurveyAtomicPhraseInputSchema
>;

export const createSurveyAtomicPhraseResponseSchema = z.object({
  phrase: atomicPhraseSchema,
});

export type CreateSurveyAtomicPhraseResponse = z.infer<
  typeof createSurveyAtomicPhraseResponseSchema
>;

export const updateSurveyAtomicMainItemOverrideInputSchema = z.object({
  organizationId: z.string(),
  surveyId: z.string(),
  mainItem: z.string().nullable(),
});

export type UpdateSurveyAtomicMainItemOverrideInput = z.infer<
  typeof updateSurveyAtomicMainItemOverrideInputSchema
>;

export const updateSurveyAtomicMainItemOverrideResponseSchema = z.object({
  mainItemOverride: z.string().nullable(),
});

export type UpdateSurveyAtomicMainItemOverrideResponse = z.infer<
  typeof updateSurveyAtomicMainItemOverrideResponseSchema
>;

export const atomicInspectionTargetSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  organizationId: z.string().nullable(),
  name: z.string(),
  quantityMode: z.string(),
  sortOrder: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const listAtomicInspectionTargetsInputSchema = z.object({
  organizationId: z.string(),
});

export const listAtomicInspectionTargetsResponseSchema = z.object({
  targets: z.array(atomicInspectionTargetSchema),
});

export const createAtomicInspectionTargetInputSchema = z.object({
  organizationId: z.string(),
  name: z.string().optional(),
  quantityMode: z.string().optional(),
});

export const createAtomicInspectionTargetResponseSchema = z.object({
  target: atomicInspectionTargetSchema,
});

export const updateAtomicInspectionTargetInputSchema = z.object({
  organizationId: z.string(),
  name: z.string().optional(),
  quantityMode: z.string().optional(),
  sortOrder: z.number().optional(),
});

export const updateAtomicInspectionTargetResponseSchema = z.object({
  target: atomicInspectionTargetSchema,
});

export const deleteAtomicInspectionTargetInputSchema = z.object({
  organizationId: z.string(),
});

export const deleteAtomicInspectionTargetResponseSchema = z.object({
  ok: z.literal(true),
});

export const deleteAtomicItemInputSchema = z.object({
  organizationId: z.string(),
});

export const deleteAtomicItemResponseSchema = z.object({
  success: z.literal(true),
  deletedItemId: z.string(),
});

export const getAtomicInspectionTemplateInputSchema = z.object({
  organizationId: z.string(),
});

export type GetAtomicInspectionTemplateInput = z.infer<
  typeof getAtomicInspectionTemplateInputSchema
>;

export const atomicInspectionTemplateActionSchema = z.object({
  actionPhraseId: z.string(),
  checkIds: z.array(z.string()).default([]),
  findingIds: z.array(z.string()).default([]),
  disclaimerIds: z.array(z.string()).default([]),
});

export type AtomicInspectionTemplateAction = z.infer<
  typeof atomicInspectionTemplateActionSchema
>;

export const atomicInspectionTemplateTargetSchema = z.object({
  targetId: z.string(),
  descriptorIds: z.array(z.string()).default([]),
  locationIds: z.array(z.string()).default([]),
  customQuantity: z.string().default(""),
  actions: z.array(atomicInspectionTemplateActionSchema).default([]),
});

export type AtomicInspectionTemplateTarget = z.infer<
  typeof atomicInspectionTemplateTargetSchema
>;

export const getAtomicInspectionTemplateResponseSchema = z.object({
  templates: z.array(atomicInspectionTemplateTargetSchema),
});

export type GetAtomicInspectionTemplateResponse = z.infer<
  typeof getAtomicInspectionTemplateResponseSchema
>;

export const upsertAtomicInspectionTemplateTargetInputSchema = z.object({
  organizationId: z.string(),
  targetId: z.string(),
  descriptorIds: z.array(z.string()).default([]),
  locationIds: z.array(z.string()).default([]),
  customQuantity: z.string().default(""),
  actions: z.array(atomicInspectionTemplateActionSchema).default([]),
});

export type UpsertAtomicInspectionTemplateTargetInput = z.infer<
  typeof upsertAtomicInspectionTemplateTargetInputSchema
>;

export const upsertAtomicInspectionTemplateTargetResponseSchema = z.object({
  template: atomicInspectionTemplateTargetSchema,
});

export type UpsertAtomicInspectionTemplateTargetResponse = z.infer<
  typeof upsertAtomicInspectionTemplateTargetResponseSchema
>;
